import {
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Typography,
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs, { Dayjs } from "dayjs";
import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { axiosInstance } from "../../utils/networking";
import ReferralStatusChip, {
  REFERRAL_PIPELINE_STATUSES,
  pipelineStatusLabels,
  pipelineStatusColors,
} from "../common/ReferralStatusChip";

const PRIMARY = "#9E1F63FF";

// "Days in pipeline" buckets. The dashboard's bounded referrals lane links in
// with `?minDays=90` for everything it had to drop.
const MIN_DAYS_OPTIONS = ["30", "90", "180", "365"];

// Pseudo status: referrals still moving through the pipeline. Same status set
// as the dashboard's Referrals lane (server `REFERRAL_LANE_STATUSES`), so the
// "View N older referrals" deep link lists exactly the rows the lane hid.
const ACTIVE_STATUS_FILTER = "in_pipeline";
const ACTIVE_STATUS_LABEL = "In Pipeline (Referred / Appt. Scheduled / In Progress)";
const IN_PIPELINE_STATUSES = ["referred", "appointment_scheduled", "in_progress"];

const statusFilterLabel = (value: string) =>
  value === ACTIVE_STATUS_FILTER
    ? ACTIVE_STATUS_LABEL
    : pipelineStatusLabels[value] || value;

// Days since the referral was made (`created_at`), the same clock as the
// dashboard's Referrals lane and its "Nd in pipeline" caption.
const daysInPipeline = (referral: any) => {
  const raw = referral?.created_at || referral?.updated_at;
  if (!raw) return 0;
  const parsed = dayjs(raw);
  return parsed.isValid() ? dayjs().diff(parsed, "day") : 0;
};

const MyReferrals = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialMinDays = searchParams.get("minDays") || "";
  const initialStatus = searchParams.get("status") || "";

  const [referrals, setReferrals] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  // A `?minDays=` deep link without an explicit `?status=` means "old and
  // still open", so it defaults the status filter to Active.
  const [statusFilter, setStatusFilter] = useState<string>(
    () =>
      initialStatus ||
      (initialMinDays ? ACTIVE_STATUS_FILTER : "all"),
  );
  const [minDaysFilter, setMinDaysFilter] = useState<string>(() =>
    MIN_DAYS_OPTIONS.includes(initialMinDays) ? initialMinDays : "all",
  );
  const [search, setSearch] = useState<string>("");
  const [typeFilter, setTypeFilter] = useState<string>("all");
  const [objectiveFilter, setObjectiveFilter] = useState<string>("all");
  const [referredByFilter, setReferredByFilter] = useState<string>("all");
  const [outcomeFilter, setOutcomeFilter] = useState<string>("all");
  const [dateFrom, setDateFrom] = useState<Dayjs | null>(null);
  const [dateTo, setDateTo] = useState<Dayjs | null>(null);
  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(25);
  const [orderBy, setOrderBy] = useState<string>("date");
  const [order, setOrder] = useState<"asc" | "desc">("desc");

  const fetchReferrals = () => {
    setLoading(true);
    axiosInstance
      .get(`/referral/pipeline`)
      .then((res) => {
        setReferrals(res.data.referrals || []);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    fetchReferrals();
  }, []);

  useEffect(() => {
    setPage(0);
  }, [
    statusFilter,
    search,
    typeFilter,
    objectiveFilter,
    referredByFilter,
    outcomeFilter,
    minDaysFilter,
    dateFrom,
    dateTo,
  ]);

  // The days filter is the one filter that is URL-addressable (the dashboard
  // links into it), so keep the param in step without pushing history entries.
  const changeMinDaysFilter = (value: string) => {
    setMinDaysFilter(value);
    const next = new URLSearchParams(searchParams);
    if (value === "all") next.delete("minDays");
    else next.set("minDays", value);
    setSearchParams(next, { replace: true });
  };

  const handleStatusChange = (referral: any, newStatus: string) => {
    axiosInstance
      .post(`/referral/status`, {
        referral_id: referral._id,
        pipeline_status: newStatus,
      })
      .then(() => {
        setReferrals(
          referrals.map((item) =>
            item._id === referral._id
              ? { ...item, pipeline_status: newStatus }
              : item,
          ),
        );
      });
  };

  const outcomeSummary = (outcome: any) => {
    if (!outcome) return "—";
    const parts = [];
    if (outcome.assistance_provided === true) parts.push("Assisted");
    if (outcome.assistance_provided === false) parts.push("No assistance");
    if (outcome.objective_achieved === true) parts.push("Objective achieved");
    if (outcome.objective_achieved === false) parts.push("Objective not met");
    return parts.length ? parts.join(" · ") : "Recorded";
  };

  const uniqueSorted = (values: string[]) =>
    Array.from(
      new Set(values.map((v) => (v || "").trim()).filter(Boolean)),
    ).sort((a, b) => a.localeCompare(b));

  const objectiveOptions = uniqueSorted(
    referrals.map((r) => r.referral_objective),
  );
  const referredByOptions = uniqueSorted(
    referrals.map((r) => r.referred_by_name),
  );
  const outcomeOptions = uniqueSorted(
    referrals.map((r) => outcomeSummary(r.outcome)),
  );

  const searchLower = search.trim().toLowerCase();
  const filtered = referrals.filter((r) => {
    if (searchLower) {
      const hay = `${r.firm_name || ""} ${r.referred_to || ""}`.toLowerCase();
      if (!hay.includes(searchLower)) return false;
    }
    if (typeFilter !== "all") {
      const t = r.referral_type === "Provider" ? "B2B" : "Resource";
      if (t !== typeFilter) return false;
    }
    if (
      objectiveFilter !== "all" &&
      (r.referral_objective || "").trim() !== objectiveFilter
    ) {
      return false;
    }
    if (
      referredByFilter !== "all" &&
      (r.referred_by_name || "").trim() !== referredByFilter
    ) {
      return false;
    }
    if (statusFilter === ACTIVE_STATUS_FILTER) {
      if (!IN_PIPELINE_STATUSES.includes(r.pipeline_status || "referred")) {
        return false;
      }
    } else if (
      statusFilter !== "all" &&
      (r.pipeline_status || "referred") !== statusFilter
    ) {
      return false;
    }
    if (minDaysFilter !== "all" && daysInPipeline(r) < Number(minDaysFilter)) {
      return false;
    }
    if (outcomeFilter !== "all" && outcomeSummary(r.outcome) !== outcomeFilter) {
      return false;
    }
    if (dateFrom || dateTo) {
      const dRaw = r.referral_date || r.created_at;
      if (!dRaw) return false;
      const d = dayjs(dRaw);
      if (dateFrom && d.isBefore(dateFrom, "day")) return false;
      if (dateTo && d.isAfter(dateTo, "day")) return false;
    }
    return true;
  });

  const countByStatus = (status: string) =>
    referrals.filter(
      (referral) => (referral.pipeline_status || "referred") === status,
    ).length;

  const filterDropdowns: {
    label: string;
    value: string;
    setValue: (v: string) => void;
    options: string[];
    getOptionLabel?: (v: string) => string;
  }[] = [
    {
      label: "Type",
      value: typeFilter,
      setValue: setTypeFilter,
      options: ["B2B", "Resource"],
    },
    {
      label: "Objective",
      value: objectiveFilter,
      setValue: setObjectiveFilter,
      options: objectiveOptions,
    },
    {
      label: "Referred By",
      value: referredByFilter,
      setValue: setReferredByFilter,
      options: referredByOptions,
    },
    {
      label: "Status",
      value: statusFilter,
      setValue: setStatusFilter,
      options: [ACTIVE_STATUS_FILTER, ...REFERRAL_PIPELINE_STATUSES],
      getOptionLabel: statusFilterLabel,
    },
    {
      label: "Days in pipeline",
      value: minDaysFilter,
      setValue: changeMinDaysFilter,
      options: MIN_DAYS_OPTIONS,
      getOptionLabel: (v) => `${v}+ days`,
    },
    {
      label: "Outcome",
      value: outcomeFilter,
      setValue: setOutcomeFilter,
      options: outcomeOptions,
    },
  ];

  const clearFilters = () => {
    setSearch("");
    setTypeFilter("all");
    setObjectiveFilter("all");
    setReferredByFilter("all");
    setStatusFilter("all");
    setOutcomeFilter("all");
    changeMinDaysFilter("all");
    setDateFrom(null);
    setDateTo(null);
  };

  const columns: {
    id: string;
    label: string;
    getValue: (r: any) => string | number;
  }[] = [
    { id: "business", label: "Business", getValue: (r) => r.firm_name || "" },
    {
      id: "referred_to",
      label: "Referred To",
      getValue: (r) => r.referred_to || "",
    },
    {
      id: "type",
      label: "Type",
      getValue: (r) => (r.referral_type === "Provider" ? "B2B" : "Resource"),
    },
    {
      id: "objective",
      label: "Objective",
      getValue: (r) => r.referral_objective || "",
    },
    {
      id: "referred_by",
      label: "Referred By",
      getValue: (r) => (r.referred_by_name || "").trim(),
    },
    {
      id: "date",
      label: "Date",
      getValue: (r) => {
        const d = r.referral_date || r.created_at;
        return d ? new Date(d).getTime() : 0;
      },
    },
    {
      id: "status",
      label: "Status",
      getValue: (r) => r.pipeline_status || "referred",
    },
    {
      id: "outcome",
      label: "Outcome",
      getValue: (r) => outcomeSummary(r.outcome),
    },
  ];

  const handleSort = (colId: string) => {
    if (orderBy === colId) {
      setOrder(order === "asc" ? "desc" : "asc");
    } else {
      setOrderBy(colId);
      setOrder("asc");
    }
    setPage(0);
  };

  const activeColumn = columns.find((c) => c.id === orderBy);
  const sorted = activeColumn
    ? [...filtered].sort((a, b) => {
        const av = activeColumn.getValue(a);
        const bv = activeColumn.getValue(b);
        let cmp: number;
        if (typeof av === "number" && typeof bv === "number") {
          cmp = av - bv;
        } else {
          cmp = String(av).localeCompare(String(bv));
        }
        return order === "asc" ? cmp : -cmp;
      })
    : filtered;

  return (
    <Box sx={{ padding: "20px", textAlign: "left" }}>
      <Typography
        variant="h5"
        sx={{ fontWeight: 700, color: "#2c2c34", mb: 2 }}
      >
        My Referrals
      </Typography>
      <Stack
        direction="row"
        spacing={1}
        sx={{ mb: 2, flexWrap: "wrap" }}
        useFlexGap
      >
        <Chip
          label={`All (${referrals.length})`}
          onClick={() => setStatusFilter("all")}
          sx={{
            fontWeight: 600,
            backgroundColor: statusFilter === "all" ? PRIMARY : undefined,
            color: statusFilter === "all" ? "white" : undefined,
            "&:hover": {
              backgroundColor: statusFilter === "all" ? PRIMARY : undefined,
            },
          }}
        />
        {REFERRAL_PIPELINE_STATUSES.map((status) => {
          const color = pipelineStatusColors[status];
          const active = statusFilter === status;
          return (
            <Chip
              key={status}
              label={`${pipelineStatusLabels[status]} (${countByStatus(status)})`}
              onClick={() => setStatusFilter(active ? "all" : status)}
              sx={{
                fontWeight: 600,
                backgroundColor: active ? color : `${color}1f`,
                color: active ? "white" : color,
                "&:hover": { backgroundColor: active ? color : `${color}33` },
              }}
            />
          );
        })}
      </Stack>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <Stack
          direction="row"
          spacing={2}
          sx={{ mb: 2, flexWrap: "wrap" }}
          useFlexGap
          alignItems="center"
        >
          <TextField
            size="small"
            label="Search name"
            placeholder="Client / resource / provider"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ minWidth: 240 }}
          />
          {filterDropdowns.map((d) => (
            <FormControl key={d.label} size="small" sx={{ minWidth: 160 }}>
              <InputLabel>{d.label}</InputLabel>
              <Select
                label={d.label}
                value={d.value}
                onChange={(e: any) => d.setValue(e.target.value)}
              >
                <MenuItem value="all">All</MenuItem>
                {d.options.map((opt) => (
                  <MenuItem key={opt} value={opt}>
                    {d.getOptionLabel ? d.getOptionLabel(opt) : opt}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          ))}
          <DatePicker
            label="From"
            value={dateFrom}
            onChange={(v) => setDateFrom(v)}
            slotProps={{ textField: { size: "small", sx: { minWidth: 150 } } }}
          />
          <DatePicker
            label="To"
            value={dateTo}
            onChange={(v) => setDateTo(v)}
            slotProps={{ textField: { size: "small", sx: { minWidth: 150 } } }}
          />
          <Button onClick={clearFilters} sx={{ color: PRIMARY }}>
            Clear
          </Button>
        </Stack>
      </LocalizationProvider>
      {loading ? (
        <Box sx={{ textAlign: "center", padding: "60px" }}>
          <CircularProgress sx={{ color: PRIMARY }} />
        </Box>
      ) : (
        <Paper
          elevation={0}
          sx={{
            border: "1px solid #ececf0",
            borderRadius: "12px",
            overflow: "auto",
          }}
        >
          <TablePagination
            component="div"
            count={filtered.length}
            page={page}
            onPageChange={(_e, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[25, 50, 100]}
          />
          <Table size="small">
            <TableHead>
              <TableRow sx={{ "& th": { fontWeight: 700, color: "#2c2c34" } }}>
                {columns.map((column) => (
                  <TableCell
                    key={column.id}
                    sortDirection={orderBy === column.id ? order : false}
                  >
                    <TableSortLabel
                      active={orderBy === column.id}
                      direction={orderBy === column.id ? order : "asc"}
                      onClick={() => handleSort(column.id)}
                    >
                      {column.label}
                    </TableSortLabel>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={8}
                    sx={{ textAlign: "center", color: "#8a8a93" }}
                  >
                    No referrals
                    {statusFilter !== "all"
                      ? ` with status "${statusFilterLabel(statusFilter)}"`
                      : ""}
                    {minDaysFilter !== "all"
                      ? ` older than ${minDaysFilter} days`
                      : ""}
                    .
                  </TableCell>
                </TableRow>
              ) : (
                sorted
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((referral) => (
                  <TableRow key={referral._id} hover>
                    <TableCell>
                      <Link
                        to={`/case?id=${referral.case_id}&referral=${referral._id}`}
                        style={{
                          color: PRIMARY,
                          fontWeight: 600,
                          textDecoration: "none",
                        }}
                      >
                        {referral.firm_name || "Unknown Business"}
                      </Link>
                    </TableCell>
                    <TableCell>{referral.referred_to || "—"}</TableCell>
                    <TableCell>
                      {referral.referral_type === "Provider"
                        ? "B2B"
                        : "Resource"}
                    </TableCell>
                    <TableCell>{referral.referral_objective || "—"}</TableCell>
                    <TableCell>
                      {(referral.referred_by_name || "").trim() || "—"}
                    </TableCell>
                    <TableCell>
                      {referral.referral_date || referral.created_at
                        ? new Date(
                            referral.referral_date || referral.created_at,
                          ).toLocaleDateString("en-US")
                        : "—"}
                    </TableCell>
                    <TableCell>
                      <FormControl size="small">
                        <Select
                          value={referral.pipeline_status || "referred"}
                          onChange={(e: any) =>
                            handleStatusChange(referral, e.target.value)
                          }
                          renderValue={(value: any) => (
                            <ReferralStatusChip status={value} />
                          )}
                          sx={{
                            "& .MuiOutlinedInput-notchedOutline": {
                              border: "none",
                            },
                          }}
                        >
                          {REFERRAL_PIPELINE_STATUSES.map((statusOption) => (
                            <MenuItem key={statusOption} value={statusOption}>
                              {pipelineStatusLabels[statusOption]}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </TableCell>
                    <TableCell>{outcomeSummary(referral.outcome)}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </Paper>
      )}
    </Box>
  );
};

export default MyReferrals;
